import type {
  DragEvent,
  FC,
  KeyboardEvent,
  MouseEvent,
  ReactNode
} from "react";

import { TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

type Props = {
  children: ReactNode;
  /** Row click / Enter / Space. When omitted the row is not interactive. */
  onActivate?: () => void;
  /** DOM id so `useFocusScroll` can scroll to the row. */
  domId?: string;
  /** Deep-link highlight. */
  highlighted?: boolean;
  className?: string;
  ariaLabel?: string;
  "data-testid"?: string;
  /** The row is a drag source (HTML drag and drop). */
  draggable?: boolean;
  onDragStart?: (event: DragEvent<HTMLTableRowElement>) => void;
  onDragEnd?: (event: DragEvent<HTMLTableRowElement>) => void;
  /** The row is a drop target. */
  onDragOver?: (event: DragEvent<HTMLTableRowElement>) => void;
  onDragLeave?: (event: DragEvent<HTMLTableRowElement>) => void;
  onDrop?: (event: DragEvent<HTMLTableRowElement>) => void;
};

export const ClientTableRow: FC<Props> = ({
  children,
  onActivate,
  domId,
  highlighted = false,
  className,
  ariaLabel,
  "data-testid": testId,
  draggable,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop
}) => {
  const interactive = !!onActivate;

  const handleClick = (event: MouseEvent<HTMLTableRowElement>) => {
    if (!onActivate) return;
    // Ignore clicks that originated in nested controls (menus, checkboxes, links).
    const target = event.target as HTMLElement;
    if (target.closest("button, a, input, [data-row-stop]")) return;
    // Menus and popovers opened from the row portal out of it but still
    // bubble here through React.
    if (!event.currentTarget.contains(target)) return;
    onActivate();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (!onActivate || event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onActivate();
    }
  };

  return (
    <TableRow
      id={domId}
      tabIndex={interactive ? 0 : undefined}
      aria-label={ariaLabel}
      data-testid={testId}
      onClick={interactive ? handleClick : undefined}
      onKeyDown={interactive ? handleKeyDown : undefined}
      draggable={draggable || undefined}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={cn(
        interactive &&
          "cursor-pointer focus:outline-hidden focus-visible:bg-fill-1",
        !interactive && !highlighted && "hover:bg-transparent",
        highlighted && "bg-accent-soft ring-1 ring-accent-line ring-inset",
        className
      )}
    >
      {children}
    </TableRow>
  );
};
